// deps
import React, { ReactElement, useMemo } from "react";

import { useApplyCoupon } from "@dineout-cart-client/hooks/useApplyCoupon";

// types
import { CouponListingContext } from "@dineout-cart-client/contexts/CouponListingContext";
import { UpdateCartContext } from "@dineout-cart-client/contexts/UpdateCartContext";
import { usePerformUpdateCart } from "@dineout-cart-client/hooks/usePerformUpdateCart";

export function CartActionProvider(props: { children: React.ReactNode }): ReactElement {
    const {
        fetchCoupons,
        isFetching,
        syncApplyCouponCart,
        isApplyingCoupon,
        couponsPayload,
        couponModalData,
        showPopup,
        onCloseModal,
        nextBestCoupon,
        errorMessage,
        clearErrorMessage,
        resetCouponListingPage,
        handleCouponListingPageCouponRemove,
        isRemovingCoupon,
        hasInitiallyFetched,
    } = useApplyCoupon();

    const { updateCart, loading: updateCartLoading, error: updateCartError } = usePerformUpdateCart();

    const updateCartContextValue = useMemo(
        () => ({
            resetCouponListingPage,
            syncApplyCouponCart,
            couponModalData: couponModalData ?? null,
            showPopup,
            handleCouponListingPageCouponRemove,
            onCloseModal,
            updateCartLoading,
            updateCartError,
            doUpdateCart: updateCart,
        }),
        [
            couponModalData,
            showPopup,
            syncApplyCouponCart,
            resetCouponListingPage,
            handleCouponListingPageCouponRemove,
            onCloseModal,
            updateCart,
            updateCartLoading,
            updateCartError,
        ],
    );

    const couponListingContextValue = useMemo(
        () => ({
            fetchCoupons,
            isFetching,
            isApplyingCoupon,
            couponsPayload,
            nextBestCoupon,
            couponModalData,
            showPopup,
            onCloseModal,
            errorMessage,
            syncApplyCouponCart,
            clearErrorMessage,
            resetCouponListingPage,
            isRemovingCoupon,
            hasInitiallyFetched,
        }),
        [
            clearErrorMessage,
            couponsPayload,
            errorMessage,
            couponModalData,
            showPopup,
            onCloseModal,
            fetchCoupons,
            syncApplyCouponCart,
            isApplyingCoupon,
            isFetching,
            nextBestCoupon,
            resetCouponListingPage,
            isRemovingCoupon,
            hasInitiallyFetched,
        ],
    );
    return (
        <UpdateCartContext.Provider value={updateCartContextValue}>
            <CouponListingContext.Provider value={couponListingContextValue}>
                {props.children}
            </CouponListingContext.Provider>
        </UpdateCartContext.Provider>
    );
}
